import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { getAuthSiteUrl } from '../authSiteUrl';
import LogoImg from '../ChatGPT Image Jun 26, 2025, 12_35_25 AM.png';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import {
  writeSignupPlanIntent,
  clearSignupPlanIntent,
  readSignupPlanIntent,
} from '../utils/signupPlanIntent';
import { PRO_MONTHLY_USD } from '../config/plans';
import { runPostAuthOnboarding } from '../services/onboardingClient';
import { DEVELLO_API_ORIGIN } from '../utils/asyncReliability';
import { devWarn, devError } from '../utils/logger';

const inputClass =
  'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';
const labelClass = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500';

const BrandPanel = () => (
  <div className="relative flex flex-col justify-between overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-950 px-8 py-10 text-white lg:min-h-screen lg:w-[44%] lg:px-12 lg:py-14">
    <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-cyan-500/15 blur-3xl" />
    <div className="pointer-events-none absolute bottom-0 left-0 h-48 w-48 rounded-full bg-primary/20 blur-3xl" />
    <div className="relative">
      <Link to="/" className="inline-flex items-center gap-3 transition-opacity hover:opacity-90">
        <img src={LogoImg} alt="" className="h-11 w-11 shrink-0 object-contain" />
        <span className="bg-gradient-to-r from-cyan-200 to-white bg-clip-text text-2xl font-bold tracking-tight text-transparent">
          DevelloTech
        </span>
      </Link>
      <h1 className="mt-10 max-w-sm text-2xl font-bold leading-snug tracking-tight text-white lg:text-3xl lg:leading-tight">
        Cost and margin on the quote—before you send it.
      </h1>
      <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
        Sign in to your workspace or create a contractor account. Real material pricing, labour, and markup in one flow.
      </p>
      <ul className="mt-8 space-y-3 text-sm text-slate-300">
        <li className="flex gap-2">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
          Built for contractors across Canada and the United States
        </li>
        <li className="flex gap-2">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
          Quote and invoice PDFs on Free; email delivery and team portal on Pro
        </li>
      </ul>
    </div>
    <p className="relative mt-12 text-xs text-slate-500 lg:mt-0">
      <Link to="/" className="text-slate-400 underline-offset-2 hover:text-white hover:underline">
        ← Back to home
      </Link>
    </p>
  </div>
);

const CardShell = ({ children, title, subtitle }) => (
  <div className="w-full max-w-md rounded-2xl border border-slate-200/80 bg-white p-8 shadow-xl shadow-slate-900/5">
    <div className="mb-8 lg:hidden">
      <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800">
        <span aria-hidden>←</span> Home
      </Link>
    </div>
    <div className="mb-8">
      <h2 className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">{title}</h2>
      {subtitle && <p className="mt-2 text-sm leading-relaxed text-slate-600">{subtitle}</p>}
    </div>
    {children}
  </div>
);

function getFriendlyError(error) {
  if (!error) return 'An unexpected error occurred.';
  if (error.code === 'PROFILE_CREATION_FAILED') return 'We could not finish setting up your account. Please try again.';
  const msg = error.message || error.error_description || '';
  if (msg.includes('Invalid login')) return 'Invalid email or password.';
  if (msg.includes('Email not confirmed')) return 'Please verify your email address before signing in.';
  if (msg.includes('User already registered')) return 'An account with this email already exists. Please sign in instead.';
  if (msg.includes('Password should be')) return 'Password is too weak.';
  if (msg.includes('rate limit') || msg.includes('too many')) return 'Too many attempts. Please try again later.';
  return msg || 'An unexpected error occurred.';
}

const Account = () => {
  const [showSignup, setShowSignup] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [chosenPlan, setChosenPlan] = useState('free');

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginMode, setLoginMode] = useState(() =>
    new URLSearchParams(window.location.search).get('mode') === 'team' ? 'team_member' : 'contractor'
  );

  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  // Team member forgot-password inline flow
  const [tmForgotMode, setTmForgotMode]   = useState(false);
  const [tmForgotEmail, setTmForgotEmail] = useState('');
  const [tmForgotMsg, setTmForgotMsg]     = useState('');
  const [tmForgotErr, setTmForgotErr]     = useState(false);
  const [tmForgotBusy, setTmForgotBusy]  = useState(false);
  const [profileSetupFailed, setProfileSetupFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const done = (session) => {
      if (cancelled) return;
      setUser(session?.user ?? null);
      setAuthLoading(false);
    };

    supabase.auth
      .getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          devWarn('Account: getSession failed', error);
          done(null);
          return;
        }
        done(session);
      })
      .catch(() => {
        done(null);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      done(session);
    });

    const safetyMs = 12000;
    const safetyId = window.setTimeout(() => {
      if (!cancelled) setAuthLoading((still) => (still ? false : still));
    }, safetyMs);

    return () => {
      cancelled = true;
      window.clearTimeout(safetyId);
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [user]);

  useEffect(() => {
    const plan = (searchParams.get('plan') || '').toLowerCase();
    if (plan === 'free' || plan === 'pro') {
      setChosenPlan(plan);
      setShowSignup(true);
      setError('');
    }
    if (searchParams.get('signup') === '1') {
      setShowSignup(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('created') === '1' && location.state?.pendingEmail) {
      setLoginEmail(String(location.state.pendingEmail));
      setShowSignup(false);
    }
  }, [searchParams, location.state]);

  // Already signed in — skip the manual "Open dashboard" step.
  useEffect(() => {
    if (authLoading || !user || showSignup || showReset || tmForgotMode) return;

    const userType = user.app_metadata?.user_type;
    if (userType === 'team_member') {
      let cancelled = false;
      (async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token;
          if (!token || cancelled) return;
          const resp = await fetch(`${DEVELLO_API_ORIGIN}/api/team/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!resp.ok) {
            if (resp.status === 401 || resp.status === 403) {
              await supabase.auth.signOut();
            }
            return;
          }
          if (!cancelled) navigate('/team-dashboard', { replace: true });
        } catch {
          /* stay on account */
        }
      })();
      return () => { cancelled = true; };
    }

    if (user.email_confirmed_at) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, user, showSignup, showReset, tmForgotMode, navigate]);

  const handleTeamMemberLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });
      if (signInError) throw signInError;

      // Block contractors from using the team member login
      const userType = data.user?.app_metadata?.user_type;
      if (userType !== 'team_member') {
        await supabase.auth.signOut();
        setError('This account belongs to a contractor. Please use the Contractor tab to sign in.');
        return;
      }

      const token = data.session?.access_token;
      const resp = await fetch(`${DEVELLO_API_ORIGIN}/api/team/me`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await resp.json();
      if (!resp.ok) {
        await supabase.auth.signOut();
        setError(json.error || 'Team member account not found or deactivated.');
        return;
      }
      navigate('/team-dashboard');
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleTmForgot = async (e) => {
    e.preventDefault();
    const trimmed = tmForgotEmail.trim().toLowerCase();
    setTmForgotBusy(true); setTmForgotMsg(''); setTmForgotErr(false);
    try {
      const resp = await fetch(`${DEVELLO_API_ORIGIN}/api/team/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error || 'Something went wrong.');
      if (json.canReset) {
        await supabase.auth.resetPasswordForEmail(trimmed, {
          redirectTo: window.location.origin + '/team-login',
        });
      }
      setTmForgotMsg('If that email belongs to an active account, a reset link has been sent.');
    } catch (err) {
      setTmForgotErr(true);
      setTmForgotMsg(err.message);
    } finally {
      setTmForgotBusy(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loginMode === 'team_member') return handleTeamMemberLogin(e);
    setLoading(true);
    setError('');
    setSuccess('');
    setProfileSetupFailed(false);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });
      if (signInError) throw signInError;
      const loggedInUser = data.user;

      // Block team members from accessing the contractor portal
      if (loggedInUser.app_metadata?.user_type === 'team_member') {
        await supabase.auth.signOut();
        setError('This account belongs to a team member. Please use the Team Member tab to sign in.');
        setLoading(false);
        return;
      }

      if (!loggedInUser.email_confirmed_at) {
        await supabase.auth.signOut();
        setError(
          'Please verify your email address before signing in. Use the "Send Verification Email" button below to receive a verification link.'
        );
        setLoading(false);
        return;
      }

      setSuccess('Signed in successfully!');
      const next = await runPostAuthOnboarding({
        allowCheckout: false,
        includeSignupPlanIntent: false,
      });
      if (next === 'dashboard') {
        navigate('/dashboard');
      }
    } catch (err) {
      if (err?.code === 'PROFILE_CREATION_FAILED') {
        devError('PROFILE_CREATION_FAILED', err);
        setProfileSetupFailed(true);
      }
      setError(getFriendlyError(err));
    }
    setLoading(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setProfileSetupFailed(false);

    if (!acceptTerms) {
      setError('You must accept the Terms of Service and Privacy Policy.');
      setLoading(false);
      return;
    }
    if (signupPassword !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }
    if (signupPassword.length < 8 || !/[!@#$%^&*(),.?":{}|<>]/.test(signupPassword)) {
      setError('Password must be at least 8 characters and include at least one symbol.');
      setLoading(false);
      return;
    }
    try {
      writeSignupPlanIntent(chosenPlan === 'pro' ? 'pro' : 'free');

      const { data, error: signUpError } = await supabase.auth.signUp({
        email: signupEmail,
        password: signupPassword,
        options: {
          emailRedirectTo: `${getAuthSiteUrl()}/verify-email`,
          data: {
            signup_plan_intent: chosenPlan === 'pro' ? 'pro' : 'free',
          },
        },
      });
      if (signUpError) throw signUpError;
      const newUser = data.user;

      const needsEmailConfirm = !!newUser && !newUser.email_confirmed_at;
      if (needsEmailConfirm && data.session) {
        try {
          await supabase.auth.signOut();
        } catch (e) {
          devWarn('Account: signOut after unconfirmed signup', e);
        }
      }

      const postSignupState = { pendingEmail: signupEmail, signupPlan: chosenPlan };

      if (needsEmailConfirm) {
        setSuccess(
          'Account created. Check your email and tap the confirmation link before signing in. Free and Pro accounts both require a verified email.'
        );
        navigate('/account?created=1', { replace: true, state: postSignupState });
      } else if (data.session?.user) {
        const next = await runPostAuthOnboarding();
        if (next === 'dashboard') {
          setSuccess('Account created. You can open your workspace now.');
          navigate('/dashboard', { replace: true });
        }
      } else {
        setSuccess(
          'Account created. Check your email for a confirmation link. Free and Pro accounts both require a verified email before sign-in.'
        );
        navigate('/account?created=1', { replace: true, state: postSignupState });
      }
    } catch (err) {
      if (err?.code === 'PROFILE_CREATION_FAILED') {
        devError('PROFILE_CREATION_FAILED', err);
        setProfileSetupFailed(true);
      }
      clearSignupPlanIntent();
      setError(getFriendlyError(err));
    }
    setLoading(false);
  };

  const handleSignOut = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await supabase.auth.signOut();
      setSuccess('Signed out successfully!');
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    } catch (err) {
      setError(err.message);
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setResetMessage('');
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${getAuthSiteUrl()}/account`,
      });
      if (resetError) throw resetError;
      setSuccess('Password reset email sent! Check your inbox.');
      setShowReset(false);
      setResetEmail('');
      setResetMessage('Password reset email sent! Check your inbox.');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const email = loginEmail.trim();
      if (!email) {
        setError('Enter your email above, then tap Send verification email.');
        setLoading(false);
        return;
      }
      const { error: resendErr } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: `${getAuthSiteUrl()}/verify-email` },
      });
      if (resendErr) throw resendErr;
      setSuccess('Verification email sent. Check your inbox.');
    } catch (err) {
      setError(getFriendlyError(err));
    }
    setLoading(false);
  };

  const showLoginForm = () => {
    setShowSignup(false);
    setError('');
  };

  const showSignupForm = () => {
    setShowSignup(true);
    setError('');
  };

  const retryProfileSetup = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setProfileSetupFailed(false);
    try {
      const next = await runPostAuthOnboarding();
      if (next === 'dashboard') {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      if (err?.code === 'PROFILE_CREATION_FAILED') {
        devError('PROFILE_CREATION_FAILED', err);
        setProfileSetupFailed(true);
      }
      setError(getFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 lg:flex-row">
      <BrandPanel />

      <main className="flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-6 lg:px-12 lg:py-14">
        {searchParams.get('created') === '1' && (
          <div className="mb-6 w-full max-w-md rounded-2xl border border-cyan-200 bg-cyan-50 px-5 py-4 text-sm text-cyan-950 shadow-sm">
            <p className="font-semibold">Next step: confirm your email</p>
            <p className="mt-1.5 leading-relaxed">
              We sent a link to <strong>{location.state?.pendingEmail || 'your inbox'}</strong>. Open it to verify your
              address, then return here and sign in.
            </p>
            {(readSignupPlanIntent() === 'free' || location.state?.signupPlan === 'free') && (
              <p className="mt-2 leading-relaxed">
                You selected <strong>Free</strong>. After you verify your email and sign in, you will have full Free
                access. You can upgrade to Pro anytime from Settings.
              </p>
            )}
            {(readSignupPlanIntent() === 'pro' || location.state?.signupPlan === 'pro') && (
              <p className="mt-2 leading-relaxed">
                You selected <strong>Pro</strong>. Your workspace stays on <strong>Free</strong> until email is
                verified and Stripe payment completes.
              </p>
            )}
          </div>
        )}
        {authLoading ? (
          <div className="w-full max-w-md rounded-2xl border border-slate-200/80 bg-white p-12 text-center shadow-lg">
            <div className="mx-auto h-9 w-9 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="mt-4 text-sm font-medium text-slate-600">Loading…</p>
          </div>
        ) : user ? (
          <div className="w-full max-w-md rounded-2xl border border-slate-200/80 bg-white p-8 text-center shadow-xl">
            <p className="text-sm font-medium text-slate-600">You’re signed in.</p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Link
                to={user.app_metadata?.user_type === 'team_member' ? '/team-dashboard' : '/dashboard'}
                className="inline-flex justify-center rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white shadow-md shadow-primary/25 hover:bg-primary-dark"
              >
                Open dashboard
              </Link>
              <button
                type="button"
                onClick={handleSignOut}
                disabled={loading}
                className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Sign out
              </button>
            </div>
          </div>
        ) : (
          <>
            {!showSignup && !showReset ? (
              <CardShell
                title={loginMode === 'team_member' ? 'Team Member Sign In' : 'Sign in'}
                subtitle={loginMode === 'team_member' ? 'Access your contractor\'s projects' : 'Welcome back. Same workspace, same numbers.'}
              >
                {/* Login mode toggle */}
                <div className="flex items-center bg-slate-100 rounded-xl p-1 gap-1 mb-6">
                  <button
                    type="button"
                    onClick={() => { setLoginMode('contractor'); setError(''); setSuccess(''); }}
                    className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${loginMode === 'contractor' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    Contractor
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLoginMode('team_member'); setError(''); setSuccess(''); }}
                    className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${loginMode === 'team_member' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    Team Member
                  </button>
                </div>
                <form className="space-y-5" onSubmit={handleLogin}>
                  <input type="email" style={{ display: 'none' }} />
                  <input type="password" style={{ display: 'none' }} />
                  <div>
                    <label htmlFor="email" className={labelClass}>
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email-field"
                      required
                      className={inputClass}
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      autoComplete="email"
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className={labelClass}>
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password-field"
                      required
                      className={inputClass}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      autoComplete="current-password"
                      data-lpignore="true"
                      data-form-type="other"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      placeholder="••••••••"
                    />
                  </div>
                  {success && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-center text-sm text-emerald-800">{success}</div>
                  )}
                  {error && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-700">{error}</div>
                  )}
                  {profileSetupFailed && (
                    <button
                      type="button"
                      onClick={retryProfileSetup}
                      disabled={loading}
                      className="w-full rounded-xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                    >
                      Retry account setup
                    </button>
                  )}
                  {error && /verify|verification/i.test(error) && (
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={loading}
                      className="w-full rounded-xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                    >
                      Send verification email
                    </button>
                  )}
                  <button
                    type="submit"
                    className="w-full rounded-xl bg-primary py-3.5 text-sm font-bold text-white shadow-lg shadow-primary/25 transition hover:bg-primary-dark disabled:opacity-60"
                    disabled={loading}
                  >
                    {loading ? 'Signing in…' : 'Sign in'}
                  </button>
                </form>
                <div className="mt-6 space-y-4 text-center text-sm">
                  {loginMode === 'contractor' && (
                    <button
                      type="button"
                      className="font-medium text-primary hover:text-primary-dark"
                      onClick={() => {
                        setShowReset(true);
                        setError('');
                        setResetMessage('');
                        setResetEmail(loginEmail);
                      }}
                    >
                      Forgot password?
                    </button>
                  )}
                  {loginMode === 'team_member' && !tmForgotMode && (
                    <div className="space-y-2">
                      <button
                        type="button"
                        className="font-medium text-primary hover:text-primary-dark"
                        onClick={() => { setTmForgotMode(true); setTmForgotEmail(loginEmail); setTmForgotMsg(''); setTmForgotErr(false); }}
                      >
                        Forgot password?
                      </button>
                      <p className="text-xs text-slate-500">Don't have an account? You need an invite link from your contractor.</p>
                    </div>
                  )}
                  {loginMode === 'team_member' && tmForgotMode && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-left space-y-3">
                      <p className="text-sm font-semibold text-slate-700">Reset your password</p>
                      <form onSubmit={handleTmForgot} className="space-y-3">
                        <input
                          type="email"
                          value={tmForgotEmail}
                          onChange={(e) => setTmForgotEmail(e.target.value)}
                          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                          placeholder="your@email.com"
                          required
                        />
                        {tmForgotMsg && (
                          <p className={`text-xs ${tmForgotErr ? 'text-red-600' : 'text-emerald-700'}`}>{tmForgotMsg}</p>
                        )}
                        <div className="flex gap-2">
                          <button
                            type="submit"
                            disabled={tmForgotBusy}
                            className="flex-1 rounded-lg bg-primary py-2 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-50 transition-colors"
                          >
                            {tmForgotBusy ? 'Sending…' : 'Send Reset Link'}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setTmForgotMode(false); setTmForgotMsg(''); }}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                  {loginMode === 'contractor' && (
                  <p className="text-slate-600">
                    No account?{' '}
                    <button type="button" onClick={showSignupForm} className="font-semibold text-primary hover:underline">
                      Create one
                    </button>
                  </p>
                  )}
                </div>
              </CardShell>
            ) : showReset ? (
              <CardShell title="Reset password" subtitle="We’ll email you a link to set a new password.">
                <form className="space-y-5" onSubmit={handleForgotPassword}>
                  <div>
                    <label htmlFor="resetEmail" className={labelClass}>
                      Email
                    </label>
                    <input
                      type="email"
                      id="resetEmail"
                      name="reset-email-field"
                      required
                      className={inputClass}
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      autoComplete="email"
                      placeholder="you@company.com"
                    />
                  </div>
                  {resetMessage && <div className="text-center text-sm text-emerald-700">{resetMessage}</div>}
                  {error && <div className="text-center text-sm text-red-600">{error}</div>}
                  {profileSetupFailed && (
                    <button
                      type="button"
                      onClick={retryProfileSetup}
                      disabled={loading}
                      className="w-full rounded-xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                    >
                      Retry account setup
                    </button>
                  )}
                  <button
                    type="submit"
                    className="w-full rounded-xl bg-primary py-3.5 text-sm font-bold text-white shadow-lg shadow-primary/25 hover:bg-primary-dark disabled:opacity-60"
                    disabled={loading}
                  >
                    {loading ? 'Sending…' : 'Send reset link'}
                  </button>
                  <button
                    type="button"
                    className="w-full text-sm font-medium text-slate-600 hover:text-slate-900"
                    onClick={() => {
                      setShowReset(false);
                      setResetMessage('');
                      setError('');
                    }}
                  >
                    ← Back to sign in
                  </button>
                </form>
              </CardShell>
            ) : (
              <CardShell
                title="Create account"
                subtitle="Pick Free or Pro first. Pro is never activated until Stripe payment succeeds."
              >
                <form className="space-y-5" onSubmit={handleSignup}>
                  <input type="email" style={{ display: 'none' }} />
                  <input type="password" style={{ display: 'none' }} />
                  <div>
                    <p className={labelClass}>Plan</p>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setChosenPlan('free')}
                        className={`rounded-2xl border px-4 py-3 text-left text-sm transition ${
                          chosenPlan === 'free'
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/25'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <span className="block font-bold text-slate-900">Free</span>
                        <span className="mt-1 block text-xs text-slate-600">Quotes, clients, and PDFs</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setChosenPlan('pro')}
                        className={`rounded-2xl border px-4 py-3 text-left text-sm transition ${
                          chosenPlan === 'pro'
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/25'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <span className="block font-bold text-slate-900">Pro</span>
                        <span className="mt-1 block text-xs text-slate-600">${PRO_MONTHLY_USD}/mo — projects, portal, email</span>
                      </button>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-slate-500">
                      Free and Pro signups both require a verified email before you can sign in. New accounts stay on
                      Free until you complete Pro checkout in Stripe after verification.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className={labelClass}>
                        First name
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="first-name-field"
                        required
                        className={inputClass}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                        placeholder="Jane"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className={labelClass}>
                        Last name
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="last-name-field"
                        required
                        className={inputClass}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="signupEmail" className={labelClass}>
                      Email
                    </label>
                    <input
                      type="email"
                      id="signupEmail"
                      name="signup-email-field"
                      required
                      className={inputClass}
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      autoComplete="email"
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="signupPassword" className={labelClass}>
                      Password
                    </label>
                    <input
                      type="password"
                      id="signupPassword"
                      name="signup-password-field"
                      required
                      className={inputClass}
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      placeholder="8+ chars & a symbol"
                    />
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className={labelClass}>
                      Confirm password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirm-password-field"
                      required
                      className={inputClass}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      placeholder="Repeat password"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className={labelClass}>
                      Company <span className="font-normal normal-case text-slate-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company-field"
                      className={inputClass}
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      autoComplete="organization"
                      placeholder="Your business name"
                    />
                  </div>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="terms"
                      required
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                    />
                    <label htmlFor="terms" className="text-sm leading-snug text-slate-600">
                      I agree to the{' '}
                      <Link to="/terms-of-service" className="font-medium text-primary hover:underline" target="_blank" rel="noreferrer">
                        Terms
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy-policy" className="font-medium text-primary hover:underline" target="_blank" rel="noreferrer">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  {error && <div className="text-center text-sm text-red-600">{error}</div>}
                  <button
                    type="submit"
                    className="w-full rounded-xl bg-primary py-3.5 text-sm font-bold text-white shadow-lg shadow-primary/25 hover:bg-primary-dark disabled:opacity-60"
                    disabled={loading}
                  >
                    {loading ? 'Creating account…' : 'Create account'}
                  </button>
                </form>
                <p className="mt-8 text-center text-sm text-slate-600">
                  Already registered?{' '}
                  <button type="button" onClick={showLoginForm} className="font-semibold text-primary hover:underline">
                    Sign in
                  </button>
                </p>
              </CardShell>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Account;
